import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Calendar, Clock, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import RoundUpToggle from "@/components/RoundUpToggle";

/**
 * A professional, welcoming Discovery Session CTA section.
 * Public — no auth required. Server-side redirect to Stripe Checkout.
 */
export default function DiscoverySession() {
  const [loading, setLoading] = useState(false);
  const [donationCents, setDonationCents] = useState(0);

  const bookNow = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/create-checkout-session", {
        origin_url: window.location.origin,
        donation_cents: donationCents,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const totalDisplay = ((9700 + donationCents) / 100).toFixed(2);

  return (
    <section
      id="discovery"
      data-testid="discovery-section"
      className="max-w-7xl mx-auto px-6 lg:px-12 py-16 md:py-24 scroll-mt-24"
    >
      <div className="surface-card armor-border p-8 md:p-16 relative overflow-hidden">
        <div className="absolute inset-0 tactical-grid opacity-20" />
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#FFD700]/8 blur-3xl" />

        <div className="relative z-10 grid lg:grid-cols-5 gap-10 items-center">
          {/* Left — Copy */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-3 mb-6">
              <Calendar size={20} weight="duotone" className="text-[#FFD700]" />
              <span className="overline">// One-on-One</span>
            </div>
            <h2 className="heading-display text-4xl md:text-5xl mb-6 leading-[0.95]">
              Book a <span className="text-[#FFD700]">Discovery Session</span>
            </h2>
            <p className="text-lg text-white/80 font-light leading-relaxed mb-6 max-w-2xl">
              A focused, one-on-one call to map your goals, review your current setup, and identify
              the highest-leverage next steps forward. Come with questions — leave with a plan.
            </p>

            <ul className="space-y-3 mb-8 max-w-xl">
              {[
                "Personal one-on-one call (approx. 45 minutes)",
                "Tailored roadmap for your specific situation",
                "Written follow-up summary within 24 hours",
                "No sales pitch, just clarity and next steps",
              ].map((f, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="flex items-start gap-3"
                >
                  <CheckCircle size={20} weight="duotone" className="text-[#FFD700] flex-shrink-0 mt-0.5" />
                  <span className="text-white/85 font-light">{f}</span>
                </motion.li>
              ))}
            </ul>

            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/50">
              <Clock size={12} /> Sessions typically book 1–2 weeks out
            </div>
          </div>

          {/* Right — Price Card + CTA */}
          <div className="lg:col-span-2">
            <div className="surface-card p-7 md:p-8 bg-black/40 backdrop-blur-sm">
              <div className="overline mb-3">// Discovery Session</div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-mono text-5xl md:text-6xl font-bold text-[#FFD700] text-glow-gold" data-testid="discovery-price">
                  $97
                </span>
                <span className="text-xs uppercase tracking-widest text-white/40">USD // ONE-TIME</span>
              </div>
              <div className="text-sm text-white/60 font-light mb-8">
                Everything included. No subscriptions, no upsells.
              </div>

              <button
                onClick={bookNow}
                disabled={loading}
                data-testid="discovery-book-btn"
                className="w-full btn-gold py-4 text-xs flex items-center justify-center gap-3 disabled:opacity-50"
              >
                {loading ? (
                  "Redirecting to secure checkout..."
                ) : (
                  <>
                    {donationCents > 0 ? `Book Now — $${totalDisplay}` : "Book Now"}
                    <ArrowRight size={16} weight="bold" />
                  </>
                )}
              </button>

              <RoundUpToggle
                basePriceCents={9700}
                onChange={setDonationCents}
                testId="discovery-roundup"
              />

              <div className="mt-5 pt-5 border-t border-white/5 space-y-2">
                <div className="flex items-center gap-2 text-[0.65rem] font-mono uppercase tracking-widest text-white/40">
                  <CheckCircle size={11} weight="duotone" className="text-[#FFD700]" />
                  Secure checkout via Stripe
                </div>
                <div className="flex items-center gap-2 text-[0.65rem] font-mono uppercase tracking-widest text-white/40">
                  <CheckCircle size={11} weight="duotone" className="text-[#FFD700]" />
                  100% satisfaction guarantee
                </div>
                <div className="flex items-center gap-2 text-[0.65rem] font-mono uppercase tracking-widest text-white/40">
                  <CheckCircle size={11} weight="duotone" className="text-[#FFD700]" />
                  Instant email confirmation
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
